import { useState } from "react";
import { FlaskConical, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const REASON = {
  target_50_hit: "50% T1", counter_target_hit: "S2 TARGET", counter_stop: "S2 STOP", sl_hit: "SL",
  tp_rupees: "₹ TP", risk_stop_rupees: "₹ STOP", cutoff_1pm: "CUTOFF",
};
const Pnl = ({ v, bold }) => (
  <span className={`font-mono ${bold ? "font-bold" : ""} ${v > 0 ? "text-[#22C55E]" : v < 0 ? "text-[#EF4444]" : "text-zinc-400"}`}>
    {v > 0 ? "+" : ""}{inr(v)}
  </span>
);

const Stat = ({ label, value, testid }) => (
  <div className="border border-zinc-800 bg-[#0a0a0a] p-2" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
    <div className="font-heading text-lg font-bold text-white">{value}</div>
  </div>
);

const AggRow = ({ label, a }) => (
  <div className="flex items-center justify-between text-[11px] font-mono border-b border-zinc-900 py-1" data-testid={`bt-agg-${label}`}>
    <span className="text-zinc-300 uppercase tracking-widest">{label}</span>
    <span className="text-zinc-500">{a.trades} trades · {a.win_pct}% win · avg <Pnl v={a.avg} /></span>
    <Pnl v={a.net} bold />
  </div>
);

export const AutoTradeBacktest = ({ cfg }) => {
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const [showTrades, setShowTrades] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auto-trade/backtest", { params: { days } });
      if (data.error) { toast.error(data.error); setR(null); } else setR(data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-800 bg-[#050505] p-4" data-testid="autotrade-backtest-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <FlaskConical className="h-3.5 w-3.5 text-[#A855F7]" /> Backtest my setup
          </div>
          <div className="text-[10px] text-zinc-600 font-mono mt-0.5">
            Replays the engine's rules with your saved lots, strategy, ₹ max risk and ₹ take-profit over recorded signals.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-zinc-800">
            {[30, 60].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                      className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${days === d ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-white"}`}
                      data-testid={`bt-days-${d}`}>{d}d</button>
            ))}
          </div>
          <button onClick={run} disabled={busy || !cfg}
                  className="inline-flex items-center gap-2 bg-[#A855F7] hover:bg-[#9333EA] disabled:opacity-50 text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest"
                  title={!cfg ? "Save your setup first" : ""} data-testid="bt-run">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Run backtest
          </button>
        </div>
      </div>

      {r && (
        <div className="mt-4 space-y-4" data-testid="bt-results">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Net P&L (est.)" value={<Pnl v={r.overall.net} bold />} testid="bt-net" />
            <Stat label="Trades" value={`${r.overall.trades}`} testid="bt-trades" />
            <Stat label="Win rate" value={`${r.overall.win_pct}%`} testid="bt-win" />
            <Stat label="Max drawdown" value={<Pnl v={r.max_drawdown} />} testid="bt-mdd" />
            <Stat label="Signals · days" value={`${r.signals} · ${r.trading_days}`} testid="bt-signals" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1">By strategy</div>
              {Object.entries(r.by_strategy).map(([k, a]) => <AggRow key={k} label={k} a={a} />)}
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1 mt-3">By index</div>
              {Object.entries(r.by_index).map(([k, a]) => <AggRow key={k} label={k} a={a} />)}
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1">Exit reasons</div>
              <div className="flex flex-wrap gap-1 mb-3" data-testid="bt-reasons">
                {Object.entries(r.exit_reasons).map(([k, n]) => (
                  <span key={k} className="border border-zinc-800 px-2 py-0.5 text-[10px] font-mono text-zinc-300">{REASON[k] || k} <span className="text-zinc-500">×{n}</span></span>
                ))}
                {Object.keys(r.exit_reasons).length === 0 && <span className="text-[10px] font-mono text-zinc-600">no trades triggered</span>}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1">Not taken</div>
              <div className="text-[10px] font-mono text-zinc-500" data-testid="bt-skipped">
                no trigger {r.skipped.no_trigger} · daily cap {r.skipped.daily_cap} · ₹ risk too big {r.skipped.risk_too_big} · cooldown {r.skipped.cooldown} · no data {r.skipped.no_data}
              </div>
              <div className="text-[9px] font-mono text-[#F59E0B]/80 mt-3 leading-relaxed" data-testid="bt-assumptions">
                ESTIMATE — {r.assumptions}
              </div>
            </div>
          </div>

          {r.equity_curve.length > 0 && (
            <div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono mb-1">Daily P&L</div>
              <div className="flex items-end gap-[3px] h-16 border-b border-zinc-800" data-testid="bt-curve">
                {(() => {
                  const mx = Math.max(...r.equity_curve.map((d) => Math.abs(d.pnl)), 1);
                  return r.equity_curve.map((d) => (
                    <div key={d.date} title={`${d.date}: ${inr(d.pnl)} · equity ${inr(d.equity)}`}
                         className={`flex-1 min-w-[4px] ${d.pnl >= 0 ? "bg-[#22C55E]/70" : "bg-[#EF4444]/70"}`}
                         style={{ height: `${Math.max(4, Math.abs(d.pnl) / mx * 100)}%` }} />
                  ));
                })()}
              </div>
            </div>
          )}

          <button onClick={() => setShowTrades(!showTrades)} className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white" data-testid="bt-toggle-trades">
            {showTrades ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} {r.trades.length} simulated trades
          </button>
          {showTrades && (
            <div className="overflow-x-auto max-h-72 overflow-y-auto border border-zinc-900" data-testid="bt-trades-table">
              <table className="w-full text-[10px] font-mono">
                <thead className="text-zinc-500 uppercase tracking-widest sticky top-0 bg-[#050505]">
                  <tr>{["Date", "Index", "Strat", "Dir", "In → Out", "Spot in → out", "Lots", "Risk pts", "Exit", "P&L"].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {r.trades.map((t, i) => (
                    <tr key={i} className="border-t border-zinc-900 text-zinc-300">
                      <td className="px-2 py-1">{t.date}</td>
                      <td className="px-2 py-1">{t.symbol}</td>
                      <td className="px-2 py-1 uppercase">{t.strategy}</td>
                      <td className="px-2 py-1">{t.strategy === "s2" ? (t.trend === "bullish" ? "SHORT" : "LONG") : (t.trend === "bullish" ? "LONG" : "SHORT")}</td>
                      <td className="px-2 py-1">{t.entry_time} → {t.exit_time}</td>
                      <td className="px-2 py-1">{t.entry_spot} → {t.exit_spot}</td>
                      <td className="px-2 py-1">{t.lots}</td>
                      <td className="px-2 py-1">{t.risk_pts}</td>
                      <td className="px-2 py-1">{REASON[t.reason] || t.reason}</td>
                      <td className="px-2 py-1"><Pnl v={t.pnl} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
